import { useMemo } from "react";
import { returnSpreadLegs } from "@/lib/businessRules/swapTypeRule";
import { TradeInterestType } from "@/lib/interfaces and Enums/ENUMSwapTradeMessage";
import tradeFromScratchStore, {
  ISwapLegTicket,
  ISwapTicket,
  returnEmptySwapTicket,
} from "@/store/tradeFromScratchStore";
import {
  SwapLegKey,
  isPerLegNominalTicket,
  returnLegDefaultsForDirection,
  returnMissingSwapTicketEntries,
  returnSwapTicketType,
  returnRequiredSwapTicketKeys,
  returnSwapDateIssue,
} from "../helper/swapTicketHelper";

// One swap ticket by id: the stored ticket, its updaters and what derives from it.
export function useSwapTicket(ticketId: string) {
  const storedTicket = tradeFromScratchStore(
    (state) => state.swapTicketsByTicketId[ticketId],
  );
  const updateTicket = tradeFromScratchStore((state) => state.updateSwapTicket);
  const updateLeg = tradeFromScratchStore((state) => state.updateSwapLeg);

  const emptyTicket = useMemo(() => returnEmptySwapTicket(), []);
  const ticket = storedTicket ?? emptyTicket;

  const updateSwapLeg = (leg: SwapLegKey, changes: Partial<ISwapLegTicket>) =>
    updateLeg(ticketId, leg, changes);

  // Currency or interest type re-stamps every convention on the leg: the defaults are
  // per currency AND type, so a floating leg never keeps a fixed leg's day count. An
  // inflation direction keeps the ZCS shape through the re-stamp.
  const handleLegConventionChange = (
    legKey: SwapLegKey,
    changes: { currency?: string; interestType?: TradeInterestType },
  ) => {
    const leg = ticket[legKey];
    const currency = changes.currency ?? leg.currency;
    const interestType = changes.interestType ?? leg.interestType;
    updateSwapLeg(legKey, {
      currency,
      interestType,
      ...returnLegDefaultsForDirection(ticket.direction, currency, interestType),
      // A rate belongs to one type only, so drop the one that no longer applies.
      couponRate: interestType === TradeInterestType.Fixed ? leg.couponRate : null,
      spread: interestType === TradeInterestType.Floating ? leg.spread : null,
    });
    // The legs no longer match the picked template.
    if (ticket.templateName) updateTicket(ticketId, { templateName: "" });
  };

  return {
    ticket,
    updateSwapTicket: (changes: Partial<ISwapTicket>) =>
      updateTicket(ticketId, changes),
    updateSwapLeg,
    handleLegConventionChange,
    swapType: returnSwapTicketType(ticket),
    isPerLegNominal: isPerLegNominalTicket(ticket),
    spreadLegs: returnSpreadLegs(ticket.leg1, ticket.leg2),
    missingEntries: returnMissingSwapTicketEntries(ticket),
    dateIssue: returnSwapDateIssue(ticket),
    requiredKeys: returnRequiredSwapTicketKeys(ticket),
  };
}
